import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  DialogActions,
  Theme,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import ModalDialog from '../../common/components/ModalDialog';
import {
  checkPoliciesChanges,
  fetchPolicySettings,
  submitPolicySettings,
} from '../actions/policy-settings-actions';
import PolicySettingsStore from '../store/policy-settings-store';
import { MdArrowForwardIos } from 'react-icons/md';
import PolicyTable from '../components/PolicyTable';
import { getCategoryById } from '../../elements/FilterBox/FilterBox-utils';
import SkeletonLoader from '../../executive-summary/components/SkeletonLoader';
import pluralize from 'pluralize';
import { makeStyles } from 'tss-react/mui';
import { generateCategoryIcon } from 'common-icons';
import { CategoryDisplayName } from '@oxappsec/ox-consolidated-categories';
import ModalConfirmationDialog from '../../common/components/ModalConfirmationDialog';

const PolicySettingsContainer: FC<
  React.PropsWithChildren<PolicySettingsContainerProps>
> = props => {
  const { policySettings, isLoading } = useSnapshot(PolicySettingsStore);
  const { appIds, appNames, open, setOpen } = props;
  useEffect(() => {
    fetchPolicySettings(appIds);
  }, [appIds]);

  const onClose = useCallback(() => {
    const hasChanges = checkPoliciesChanges();
    if (hasChanges) {
      setConfirmationOpen(true);
    } else {
      setOpen(false);
    }
  }, [setOpen]);
  const onSubmit = useCallback(() => {
    submitPolicySettings(appIds);
    setOpen(false);
    setConfirmationOpen(false);
  }, [appIds, setOpen]);

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const onConfirmationClose = useCallback(() => {
    setConfirmationOpen(false);
    setOpen(false);
  }, [setOpen]);

  const { classes } = useStyles();
  return (
    <>
      <ModalConfirmationDialog
        dialogBody='You have unsaved changes.'
        isOpen={confirmationOpen}
        title='Unsaved settings'
        cancelButtonText='Close'
        confirmationButtonText='Save'
        onCloseConfirmationModal={onConfirmationClose}
        onConfirmation={onSubmit}
      />
      <ModalDialog
        fullScreen={true}
        onClose={onClose}
        modalHeader='Pipeline Settings'
        modalFooter={
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={onSubmit}>Save</Button>
          </DialogActions>
        }
        open={open}>
        <SkeletonLoader loading={isLoading}>
          <Typography sx={{ m: 1 }} variant='body1' color='text.primary'>
            Configure pipeline settings for{' '}
            {pluralize('app', appIds.length, true)}:{' '}
            {appNames.slice(0, 3).join(', ')} {appNames.length > 3 && '...'}
          </Typography>
          {policySettings.map(category => {
            const categoryName = getCategoryById(category.categoryId);
            const CategoryIcon = generateCategoryIcon(
              categoryName as CategoryDisplayName,
            );

            return (
              <Accordion
                key={category.categoryId}
                sx={{ width: '100%' }}
                TransitionProps={{ unmountOnExit: true, mountOnEnter: false }}>
                <AccordionSummary
                  expandIcon={<MdArrowForwardIos />}
                  className={classes.accordionSummary}>
                  <>
                    {CategoryIcon && <CategoryIcon width={12} height={12} />}
                    {categoryName}
                  </>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <PolicyTable
                    categoryId={category.categoryId}
                    settings={category.settings}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </SkeletonLoader>
      </ModalDialog>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  accordionSummary: {
    gap: theme.spacing(1),
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
  accordionDetails: {
    width: '100%',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

export interface PolicySettingsContainerProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  appIds: string[];
  appNames: string[];
}

export default PolicySettingsContainer;
